import type { DailyCheckIn } from '../types/localStorage';

// ─────────────────────────────────────────────────
// Pattern Detection — Analyze check-in, goal, and timer data
// Used by weekly report and evening check-in pattern insights
// ─────────────────────────────────────────────────

export interface WeekdayPattern {
  dayIndex: number;
  dayName: string;
  avgEnergy: number;
  completionRate: number;
  count: number;
}

export interface StreakInfo {
  type: 'achievement' | 'miss';
  days: number;
  current: boolean;
}

export interface FocusTimePattern {
  period: string;
  totalMinutes: number;
  sessionCount: number;
}

export interface PatternInsight {
  type: 'energy' | 'streak' | 'focus' | 'completion' | 'improvement';
  message: string;
  data?: Record<string, unknown>;
}

export interface WeeklyComparison {
  metric: string;
  thisWeek: number;
  lastWeek: number;
  change: number;
  direction: 'up' | 'down' | 'same';
}

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

/** Get the ISO date string for n days ago */
function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

/** Filter check-ins within a date range */
function filterByDateRange(checkIns: DailyCheckIn[], startDate: string, endDate: string): DailyCheckIn[] {
  return checkIns.filter(c => c.date >= startDate && c.date <= endDate);
}

/** Analyze weekday energy patterns */
export function analyzeWeekdayEnergy(checkIns: DailyCheckIn[], days: number = 30): WeekdayPattern[] {
  const cutoff = daysAgo(days);
  const recent = checkIns.filter(c => c.date >= cutoff && c.morning?.energyLevel);

  const byDay: Record<number, { totalEnergy: number; totalDone: number; totalItems: number; count: number }> = {};
  for (let i = 0; i < 7; i++) byDay[i] = { totalEnergy: 0, totalDone: 0, totalItems: 0, count: 0 };

  for (const c of recent) {
    const dayIdx = new Date(c.date).getDay();
    if (c.morning?.energyLevel) {
      byDay[dayIdx].totalEnergy += c.morning.energyLevel;
      byDay[dayIdx].count++;
    }
    if (c.evening?.top3Status) {
      const done = c.evening.top3Status.filter(s => s === 'done').length;
      byDay[dayIdx].totalDone += done;
      byDay[dayIdx].totalItems += c.evening.top3Status.length;
    }
  }

  return Object.entries(byDay).map(([idx, data]) => ({
    dayIndex: Number(idx),
    dayName: DAY_NAMES[Number(idx)],
    avgEnergy: data.count > 0 ? Math.round((data.totalEnergy / data.count) * 10) / 10 : 0,
    completionRate: data.totalItems > 0 ? Math.round((data.totalDone / data.totalItems) * 100) : 0,
    count: data.count,
  }));
}

/** Detect achievement and miss streaks */
export function detectStreaks(checkIns: DailyCheckIn[]): StreakInfo[] {
  const sorted = [...checkIns]
    .filter(c => c.evening?.top3Status)
    .sort((a, b) => b.date.localeCompare(a.date));

  if (sorted.length === 0) return [];

  const streaks: StreakInfo[] = [];
  let currentType: 'achievement' | 'miss' | null = null;
  let currentDays = 0;

  for (const c of sorted) {
    const allDone = c.evening!.top3Status.every(s => s === 'done');
    const type = allDone ? 'achievement' : 'miss';

    if (type === currentType) {
      currentDays++;
    } else {
      if (currentType !== null) {
        streaks.push({ type: currentType, days: currentDays, current: streaks.length === 0 });
      }
      currentType = type;
      currentDays = 1;
    }
  }

  if (currentType !== null) {
    streaks.push({ type: currentType, days: currentDays, current: streaks.length === 0 });
  }

  return streaks;
}

/** Compare this week vs last week */
export function computeWeeklyComparison(
  checkIns: DailyCheckIn[],
  timerSessions: Array<{ duration: number; date?: string; startTime?: string }>,
  goalCompleteDays: { thisWeek: number; lastWeek: number },
): WeeklyComparison[] {
  const today = daysAgo(0);
  const weekAgo = daysAgo(7);
  const twoWeeksAgo = daysAgo(14);

  const thisWeekCIs = filterByDateRange(checkIns, weekAgo, today);
  const lastWeekCIs = filterByDateRange(checkIns, twoWeeksAgo, weekAgo);

  // Check-in count
  const thisWeekCheckIns = thisWeekCIs.length;
  const lastWeekCheckIns = lastWeekCIs.length;

  // Average energy
  const thisWeekEnergies = thisWeekCIs.filter(c => c.morning?.energyLevel).map(c => c.morning!.energyLevel);
  const lastWeekEnergies = lastWeekCIs.filter(c => c.morning?.energyLevel).map(c => c.morning!.energyLevel);
  const thisWeekAvgEnergy = thisWeekEnergies.length > 0
    ? Math.round((thisWeekEnergies.reduce((s, v) => s + v, 0) / thisWeekEnergies.length) * 10) / 10
    : 0;
  const lastWeekAvgEnergy = lastWeekEnergies.length > 0
    ? Math.round((lastWeekEnergies.reduce((s, v) => s + v, 0) / lastWeekEnergies.length) * 10) / 10
    : 0;

  // Focus minutes
  const getSessionDate = (s: { date?: string; startTime?: string }) => {
    if (s.date) return s.date;
    if (s.startTime) return s.startTime.split('T')[0];
    return '';
  };
  const thisWeekFocus = timerSessions
    .filter(s => { const d = getSessionDate(s); return d >= weekAgo && d <= today; })
    .reduce((sum, s) => sum + (s.duration || 0), 0);
  const lastWeekFocus = timerSessions
    .filter(s => { const d = getSessionDate(s); return d >= twoWeeksAgo && d < weekAgo; })
    .reduce((sum, s) => sum + (s.duration || 0), 0);

  const makeComparison = (metric: string, thisWeek: number, lastWeek: number): WeeklyComparison => ({
    metric,
    thisWeek,
    lastWeek,
    change: lastWeek === 0 ? (thisWeek > 0 ? 100 : 0) : Math.round(((thisWeek - lastWeek) / lastWeek) * 100),
    direction: thisWeek > lastWeek ? 'up' : thisWeek < lastWeek ? 'down' : 'same',
  });

  return [
    makeComparison('체크인', thisWeekCheckIns, lastWeekCheckIns),
    makeComparison('집중 시간(분)', Math.round(thisWeekFocus / 60), Math.round(lastWeekFocus / 60)),
    makeComparison('목표 달성일', goalCompleteDays.thisWeek, goalCompleteDays.lastWeek),
    makeComparison('평균 에너지', thisWeekAvgEnergy, lastWeekAvgEnergy),
  ];
}

/** Generate pattern insights from check-in data */
export function computeCheckInPatterns(checkIns: DailyCheckIn[]): PatternInsight[] {
  const insights: PatternInsight[] = [];

  // Streak detection
  const streaks = detectStreaks(checkIns);
  const currentStreak = streaks.find(s => s.current);
  if (currentStreak && currentStreak.type === 'achievement' && currentStreak.days >= 3) {
    insights.push({
      type: 'streak',
      message: `${currentStreak.days}일 연속 TOP 3 완벽 달성!`,
      data: { days: currentStreak.days },
    });
  }
  if (currentStreak && currentStreak.type === 'miss' && currentStreak.days >= 3) {
    insights.push({
      type: 'streak',
      message: `최근 ${currentStreak.days}일 동안 목표 미달성이 이어지고 있어요. 목표를 조금 줄여볼까요?`,
      data: { days: currentStreak.days },
    });
  }

  // Weekday energy pattern
  const weekdayPatterns = analyzeWeekdayEnergy(checkIns);
  const lowEnergyDays = weekdayPatterns.filter(p => p.count >= 2 && p.avgEnergy > 0 && p.avgEnergy <= 2);
  for (const day of lowEnergyDays) {
    insights.push({
      type: 'energy',
      message: `${day.dayName}요일에 에너지가 계속 낮아요 (평균 ${day.avgEnergy}) — 가벼운 목표로 시작해볼까요?`,
      data: { dayName: day.dayName, avgEnergy: day.avgEnergy },
    });
  }

  // High completion days
  const highCompletionDays = weekdayPatterns.filter(p => p.count >= 2 && p.completionRate >= 80);
  for (const day of highCompletionDays) {
    insights.push({
      type: 'completion',
      message: `${day.dayName}요일 달성률이 ${day.completionRate}%로 높아요! 이 요일의 루틴이 잘 맞는 것 같아요.`,
      data: { dayName: day.dayName, completionRate: day.completionRate },
    });
  }

  // Recent energy improvement
  const recent7 = checkIns.filter(c => c.date >= daysAgo(7) && c.morning?.energyLevel);
  const prev7 = checkIns.filter(c => c.date >= daysAgo(14) && c.date < daysAgo(7) && c.morning?.energyLevel);
  if (recent7.length >= 3 && prev7.length >= 3) {
    const recentAvg = recent7.reduce((s, c) => s + c.morning!.energyLevel, 0) / recent7.length;
    const prevAvg = prev7.reduce((s, c) => s + c.morning!.energyLevel, 0) / prev7.length;
    if (recentAvg > prevAvg + 0.5) {
      insights.push({
        type: 'improvement',
        message: `최근 에너지 레벨이 올라가고 있어요! 좋은 흐름이에요.`,
      });
    }
  }

  return insights;
}

// ─── Actionable Insight Recommendations ─────────────────
export interface ActionableRecommendation {
  trigger: string;        // what pattern was detected
  suggestion: string;     // what to do about it
  href: string;           // where to go
  ctaText: string;        // button label
}

/**
 * Generate actionable recommendations from detected patterns.
 * Goes beyond "what happened" to suggest "what to do next".
 */
export function getActionableRecommendations(checkIns: DailyCheckIn[]): ActionableRecommendation[] {
  const recommendations: ActionableRecommendation[] = [];

  const weekdayPatterns = analyzeWeekdayEnergy(checkIns);
  const todayDayIndex = new Date().getDay();
  const todayPattern = weekdayPatterns.find(p => p.dayIndex === todayDayIndex);

  // Low energy day today → suggest lighter goals
  if (todayPattern && todayPattern.count >= 2 && todayPattern.avgEnergy > 0 && todayPattern.avgEnergy <= 2.5) {
    recommendations.push({
      trigger: `${todayPattern.dayName}요일은 보통 에너지가 낮은 날이에요`,
      suggestion: '오늘은 가벼운 목표 위주로 진행해보세요',
      href: '/goals',
      ctaText: '목표 확인',
    });
  }

  // High energy day today → suggest tackling hard goals
  if (todayPattern && todayPattern.count >= 2 && todayPattern.avgEnergy >= 4) {
    recommendations.push({
      trigger: `${todayPattern.dayName}요일은 보통 에너지가 좋은 날이에요`,
      suggestion: '어려운 과제를 오늘 집중해서 해보는 건 어때요?',
      href: '/timer',
      ctaText: '집중 시작',
    });
  }

  // Streak miss pattern → suggest goal adjustment
  const streaks = detectStreaks(checkIns);
  const currentStreak = streaks.find(s => s.current);
  if (currentStreak && currentStreak.type === 'miss' && currentStreak.days >= 3) {
    recommendations.push({
      trigger: `${currentStreak.days}일 연속 목표 미달성`,
      suggestion: '목표 난이도를 낮추거나 개수를 줄여보세요. 작은 성공이 쌓여야 해요.',
      href: '/goals',
      ctaText: '목표 조정',
    });
  }

  // Low completion rate on specific day → suggest schedule adjustment
  const lowCompletionDays = weekdayPatterns.filter(
    p => p.count >= 3 && p.completionRate > 0 && p.completionRate < 30
  );
  for (const day of lowCompletionDays) {
    if (day.dayIndex === todayDayIndex) {
      recommendations.push({
        trigger: `${day.dayName}요일 달성률이 ${day.completionRate}%로 낮아요`,
        suggestion: '이 요일에는 목표를 줄이거나 복습 위주로 해보세요',
        href: '/checkin',
        ctaText: '오늘 계획 세우기',
      });
    }
  }

  return recommendations;
}
